// API client para apps.av_production en mcp_django.
// Backend: /api/v1/admin/av/* (auth Bearer JWT, scope sales/av).

const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

export class AvApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const jwt = await getJwt();
  const headers: Record<string, string> = { Authorization: `Bearer ${jwt}` };
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    throw new AvApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

async function djangoUpload<T = any>(path: string, formData: FormData): Promise<T> {
  const jwt = await getJwt();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}` },
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    // err.detail puede ser string O array (ninja validation errors)
    let msg: string;
    if (typeof err.detail === 'string') {
      msg = err.detail;
    } else if (Array.isArray(err.detail)) {
      msg = err.detail
        .map((e: any) => `${(e.loc || []).join('.')}: ${e.msg}`)
        .join('; ');
    } else if (err.detail) {
      msg = JSON.stringify(err.detail).slice(0, 300);
    } else {
      msg = `Upload failed: ${res.status}`;
    }
    throw new AvApiError(msg, res.status);
  }
  return res.json();
}

const BASE = '/api/v1/admin/av';

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export type UploadAssetResult = {
  media_id: number;
  asset_key: string;
  url: string;
  alt_text: string;
  vision_description: string;
};

export type AVProject = {
  id: number;
  org_id: string;
  title: string;
  output_type: string;
  format: string;
  status: string;
  duration_target_sec: number;
  language: string;
  global_settings: Record<string, any>;
  distribution: Record<string, any>;
  brand: Record<string, any>;
  ai_modules: Record<string, any>;
  content_sources: Record<string, any>;
  created_at: string;
  updated_at: string;
  scene_count: number;
  job_count: number;
};

export type CostBreakdown = {
  per_scene?: { scene_key: string; image: number; video: number; voice: number }[];
  totals?: { image: number; video: number; voice: number; total_usd: number };
  currency?: string;
};

export type ProducePromoResult = {
  status: string; // 'queued' | 'completed' | 'failed'
  project_id: number;
  final_video_url?: string;
  scene_clip_urls?: string[];
  scene_voice_urls?: string[];
  failed_scenes?: string[];
  total_scenes?: number;
  scenes_ok?: number;
  cost_breakdown?: CostBreakdown;
  detail?: string;
};

// ═══════════════════════════════════════════════════════════════
// Endpoints
// ═══════════════════════════════════════════════════════════════

export async function uploadAsset(
  file: File,
  asset_key: string,
  project_id?: number,
): Promise<UploadAssetResult> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('asset_key', asset_key);
  if (project_id) fd.append('project_id', String(project_id));
  return djangoUpload<UploadAssetResult>(`${BASE}/upload-asset`, fd);
}

export async function producePromoFromScript(
  spec: Record<string, any>,
  options: {
    project_id?: number;
    sync?: boolean;
    force_regenerate_scene_keys?: string[];
  } = {},
): Promise<ProducePromoResult> {
  return djangoFetch<ProducePromoResult>(`${BASE}/produce-promo-from-script`, {
    method: 'POST',
    body: JSON.stringify({
      spec,
      project_id: options.project_id ?? 0,
      sync: options.sync ?? false,
      force_regenerate_scene_keys: options.force_regenerate_scene_keys ?? [],
    }),
  });
}

export async function listProjects(status = ''): Promise<AVProject[]> {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return djangoFetch<AVProject[]>(`${BASE}/projects${q}`);
}

export async function getProject(projectId: number): Promise<AVProject> {
  return djangoFetch<AVProject>(`${BASE}/projects/${projectId}`);
}

export type AVScene = {
  id: number;
  scene_key: string;
  order: number;
  duration_sec: number;
  type: string;
  status: string; // pending | generating | done | failed
  narrative: Record<string, any>;
  visual: Record<string, any>;
  audio_config: Record<string, any>;
  transitions: Record<string, any>;
  cua_annotations: Record<string, any>;
  created_at: string;
  updated_at: string;
};

export async function listScenes(projectId: number): Promise<AVScene[]> {
  return djangoFetch<AVScene[]>(`${BASE}/projects/${projectId}/scenes`);
}

export type AssetMatch = {
  asset_key: string;
  media_id: number;
  url: string;
  alt_text: string;
  ai_description_short: string;
  found: boolean;
};

export async function resolveAssets(asset_keys: string[]): Promise<AssetMatch[]> {
  return djangoFetch<AssetMatch[]>(`${BASE}/resolve-assets`, {
    method: 'POST',
    body: JSON.stringify({ asset_keys }),
  });
}
